import type { Dictionary } from "lodash";
import countBy from "lodash/countBy";
import flow from "lodash/flow";
import max from "lodash/max";
import values from "lodash/values";
import {
	advertiserExclusionIds,
	desktopThreshold,
	mobileThreshold,
	slotSubstringExclusions,
} from "@/config/reach/common/takeover.json";
import { gptSlotResponseStore } from "@/store/gptSlotResponseStore";
import { isResponseInformation } from "@/ts/guards";
import type { TakeoverDetectionModule } from "@/ts/module";
import type {
	GPTSlotResponseStore,
	ResponseInformation,
	SlotResponseData,
	TakeoverData,
	TakeoverSlot,
} from "@/ts/types";
import { getGoogleTag } from "@/utils/googletag";
import { checkRecommenderSlot } from "@/utils/slots";

type EventTypeMap = {
	slotResponseReceived: googletag.events.SlotResponseReceived;
};

const TOP_SLOT_ID = "div-gpt-ad-top-slot";

export class TakeoverDetection implements TakeoverDetectionModule {
	name = "TakeoverDetection" as const;

	async init(): Promise<void> {
		const gt = getGoogleTag();
		const subscribeToEvent = <K extends keyof EventTypeMap>(
			eventType: K,
			handler: (event: EventTypeMap[K]) => void,
		) => {
			gt.pubads().addEventListener(eventType, handler);
		};

		gt.cmd.push(() => {
			subscribeToEvent("slotResponseReceived", onResponseReceived);
		});

		await Promise.resolve();
	}
}

const onResponseReceived = (event: googletag.events.SlotResponseReceived) => {
	const { slot } = event;
	const slotId = slot.getSlotElementId();
	const responseInformation = slot.getResponseInformation();
	const isRecommenderSlot = checkRecommenderSlot(slotId);
	const { actions } = gptSlotResponseStore.getState();
	if (!isRecommenderSlot && isResponseInformation(responseInformation)) {
		actions.setSlotsResponseInformation({
			[slotId]: {
				responseInformation,
				received: true,
			},
		});

		updateTakeoverDetection();
	}
};

const isSlotExcludedBySubstring = (slotId: string): boolean => {
	return slotSubstringExclusions.some((substring) =>
		slotId.includes(substring),
	);
};

const getResponseInformations = (
	state: GPTSlotResponseStore,
): (ResponseInformation & { slotId: string })[] => {
	const entries = Object.entries(state.slotsResponseInformation);
	return entries
		.map(([slotId, data]) => {
			if (!data?.responseInformation) {
				return null;
			}
			return {
				...data.responseInformation,
				slotId,
			};
		})
		.filter(
			(item): item is ResponseInformation & { slotId: string } => item !== null,
		);
};

const transformSlotsFormatToTakeoverDataFormat = (
	advertiserExclusionIds: number[],
	gptSlotsState: GPTSlotResponseStore,
): TakeoverData => {
	const responseInformations = getResponseInformations(gptSlotsState);

	const filteredResponseInformations = responseInformations.filter(
		(responseInformation) =>
			responseInformation &&
			responseInformation.sourceAgnosticLineItemId != null &&
			!isSlotExcludedBySubstring(responseInformation.slotId),
	);
	const takeoverSlots: TakeoverSlot[] = filteredResponseInformations.map(
		(responseInformation) => {
			const {
				isBackfill = false,
				advertiserId = null,
				sourceAgnosticLineItemId = 0,
				slotId = "",
			} = responseInformation || {};
			const isAdvertiserBackfill =
				isBackfill || advertiserExclusionIds.includes(advertiserId as number);
			return {
				takeoverExcluded: isAdvertiserBackfill,
				lineItemId: sourceAgnosticLineItemId,
				slotId: slotId,
			};
		},
	);
	return { takeoverSlots };
};

const detectSponsorship = (
	slots: TakeoverData,
	mobileThreshold: number,
	desktopThreshold: number,
) => {
	let isSponsorship = false;
	const potentialTakeoverLineItems = slots.takeoverSlots
		.filter((slot) => !slot.takeoverExcluded)
		.map((slot) => slot.lineItemId);
	const adSlotTotal = slots.takeoverSlots.length;
	const scoreRequired = adSlotTotal <= 5 ? mobileThreshold : desktopThreshold;
	if (potentialTakeoverLineItems.length > 0) {
		const lineItemIdCounts = countBy(potentialTakeoverLineItems);
		const countMostFrequent: (counts: Dictionary<number>) => number = flow(
			values,
			(counts) => max(counts) as number,
		);
		const numSlotsMostFrequentAdvertiser = countMostFrequent(lineItemIdCounts);
		const advTakeoverPercentage = Math.round(
			(100 * numSlotsMostFrequentAdvertiser) / adSlotTotal,
		);
		isSponsorship = advTakeoverPercentage >= scoreRequired;
	}
	return isSponsorship;
};

const hasResponseForSlotId =
	(slotId: string) =>
	(gptSlotResponseStore: GPTSlotResponseStore): boolean => {
		const slot = getSlotById(slotId, gptSlotResponseStore);
		return !!slot?.received;
	};

const getSlotById = (
	slotId: string,
	gptSlotResponseStore: GPTSlotResponseStore,
): SlotResponseData => {
	return gptSlotResponseStore.slotsResponseInformation[slotId];
};

const updateTakeoverDetection = () => {
	const gptSlotsState = gptSlotResponseStore.getState();
	const { actions } = gptSlotsState;

	const hasResponseReceived = hasResponseForSlotId(TOP_SLOT_ID)(gptSlotsState);
	if (hasResponseReceived) {
		const slots = transformSlotsFormatToTakeoverDataFormat(
			advertiserExclusionIds,
			gptSlotsState,
		);
		const eligibleTakeoverSlots = slots.takeoverSlots.filter(
			(slot) => !slot.takeoverExcluded,
		);

		const sponsorshipDetected = detectSponsorship(
			slots,
			mobileThreshold,
			desktopThreshold,
		);
		const takeoverSlotIds = sponsorshipDetected
			? eligibleTakeoverSlots.map((slot) => slot.slotId)
			: [];

		actions.setTakeoverDetection(takeoverSlotIds, sponsorshipDetected);
	}
};
